import { Dispatch, SetStateAction } from 'react';
import * as R from 'ramda';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestsFilters } from '@atom/types/assetRequest';
import {
  convertDateToLocalDayEnd,
  convertDateToLocalDayStart,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  DateRange,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import {
  ARDOT_118_STATUS_OPTIONS,
  STATUS_LABELS,
} from '../assetRequestConstants';

import classes from './ardotFilters.module.css';

const { MenuItem } = Select;

const styles = {
  input: {
    marginTop: '1rem',
  },
  label: {
    color: colors.neutral.dim,
  },
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
};

interface Props {
  filtersCart: AssetRequestsFilters;
  setFiltersCart: Dispatch<SetStateAction<AssetRequestsFilters>>;
  createdPresetSelected?: DateRange;
  setCreatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
  updatedPresetSelected?: DateRange;
  setUpdatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
}

const ARDOT118TransferFilters = ({
  filtersCart,
  setFiltersCart,
  createdPresetSelected,
  setCreatedPresetSelect,
  updatedPresetSelected,
  setUpdatedPresetSelect,
}: Props) => {
  const updateFilter = (value: any, property: keyof AssetRequestsFilters) => {
    setFiltersCart(prev => ({ ...prev, [property]: value }));
  };

  const getDateValue = (property: keyof AssetRequestsFilters) => {
    if (!isNilOrEmpty(filtersCart[property])) {
      return new Date(Number(filtersCart[property]));
    }

    return null;
  };

  const handleDateChange =
    (property: keyof AssetRequestsFilters, terminator: DateRangeTerminator) =>
    (val: Date) => {
      const dateMillis = val
        ? terminator === DateRangeTerminator.START
          ? convertDateToLocalDayStart(val)
          : convertDateToLocalDayEnd(val)
        : null;
      updateFilter(dateMillis, property);
    };

  const statusOptions = ARDOT_118_STATUS_OPTIONS;

  return (
    <>
      <div>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={Number(filtersCart.name)}
          placeholder="Enter number only"
          label="Request ID"
          onChange={event => updateFilter(String(event.target.value), 'name')}
        />
      </div>
      <div className={classes.inputContainer}>
        <Select
          key="status"
          multiple
          displayEmpty
          label="Status"
          value={filtersCart?.statuses || []}
          onChange={event => updateFilter(event.target.value, 'statuses')}
          renderValue={R.length(filtersCart?.statuses) > 0 ? null : () => 'All'}
          inputLabelSx={inputLabelSx}
        >
          {statusOptions.map(status => (
            <MenuItem key={status} value={status}>
              {STATUS_LABELS[status]}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.reviewer}
          updateValue={newValue => updateFilter(newValue, 'reviewer')}
          placeholder="Search users"
          label="Reviewer"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.receiver}
          updateValue={newValue => updateFilter(newValue, 'receiver')}
          placeholder="Search users"
          label="Receiver"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.createdBy}
          updateValue={newValue => updateFilter(newValue, 'createdBy')}
          placeholder="Search users"
          label="Created By"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Created Date"
          presetSelected={createdPresetSelected}
          startDateValue={getDateValue('createdDateStart')}
          handleStartChange={handleDateChange(
            'createdDateStart',
            DateRangeTerminator.START,
          )}
          endDateValue={getDateValue('createdDateEnd')}
          handleEndChange={handleDateChange(
            'createdDateEnd',
            DateRangeTerminator.END,
          )}
          handleSelectDatePreset={selected =>
            setCreatedPresetSelect(selected?.value)
          }
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Resolution Date"
          presetSelected={updatedPresetSelected}
          startDateValue={getDateValue('resolutionDateStart')}
          handleStartChange={handleDateChange(
            'resolutionDateStart',
            DateRangeTerminator.START,
          )}
          endDateValue={getDateValue('resolutionDateEnd')}
          handleEndChange={handleDateChange(
            'resolutionDateEnd',
            DateRangeTerminator.END,
          )}
          handleSelectDatePreset={selected =>
            setUpdatedPresetSelect(selected?.value)
          }
        />
      </div>
    </>
  );
};

export default ARDOT118TransferFilters;
